/**
 * The sequence viewer's sound through Web Audio (0.96.0):
 *
 *   media element ─ MediaElementSource ─ GainNode (clip gain, fades, mute/solo, pre-roll) ─┐
 *   media element ─ …                                                                     ├─ program bus (stereo) ─ monitor (volume, mute) ─ speakers
 *                                                                                         │        └─ ChannelSplitter ─ AnalyserNode L / R ─ the meter
 *
 * One AudioContext for the viewer, made at the first Play (a user gesture); each element gets its source node once
 * (an element can only ever have one): when React attaches it, or at that first Play for elements already there. The element's own volume stays at 1; mediaSync's setVolume writes the element's gain node
 * (routeVolume), which also plays clip gains above 0 dB. The meter reads the analysers on the viewer's animation
 * frames while playing (so it freezes while paused) and is measured before the monitor volume, so turning the
 * viewer down does not change the readings.
 *
 * Media elements need crossOrigin="anonymous" (the media origin sends Access-Control-Allow-Origin), or Web Audio
 * would only get silence from them. Where Web Audio is missing or refuses an element, the element keeps playing
 * through el.volume as before (at most 0 dB, no meter).
 */
import { routeVolume } from './mediaSync';
import { LoudnessMeter, PeakMeter } from './loudness';
import { createStore, type Store } from './playheadClock';

export interface MeterReading {
  /** Per channel (L, R): the bar (dBFS), the held peak (dBFS) and whether it went over. */
  channels: Array<{ level: number; hold: number; over: boolean }>;
  momentary: number;
  shortTerm: number;
  integrated: number;
  /** Whether Web Audio is running (false: no meter in this browser yet, or not started). */
  live: boolean;
}

export const EMPTY_READING: MeterReading = {
  channels: [{ level: -Infinity, hold: -Infinity, over: false }, { level: -Infinity, hold: -Infinity, over: false }],
  momentary: -Infinity, shortTerm: -Infinity, integrated: -Infinity, live: false,
};

/** The analysers' buffer: the longest gap between two animation frames that loses no samples (~0.7 s at 48 kHz). */
const FFT = 32768;
/** How quickly a gain change is followed (seconds; a few ms avoids clicks at cuts and in fades). */
const GAIN_TIME = 0.006;

type Ctor = typeof AudioContext;

export class PreviewAudio {
  readonly meter: Store<MeterReading> = createStore(EMPTY_READING);
  private ctx: AudioContext | null = null;
  private program: GainNode | null = null;
  private monitor: GainNode | null = null;
  private analysers: AnalyserNode[] = [];
  private bufs: Float32Array<ArrayBuffer>[] = [];
  private routes = new WeakMap<HTMLMediaElement, { source: MediaElementAudioSourceNode; gain: GainNode }>();
  private connected = new WeakSet<HTMLMediaElement>();
  private pending = new Set<HTMLMediaElement>();
  private lastTime = 0;
  private loudness: LoudnessMeter | null = null;
  private peaks = [new PeakMeter(), new PeakMeter()];
  private monitorLevel = { volume: 1, muted: false };
  private failed = false;

  private context(): AudioContext | null {
    if (this.ctx || this.failed) return this.ctx;
    const AC: Ctor | undefined = window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
    if (!AC) { this.failed = true; return null; }
    try {
      const ctx = new AC();
      const program = ctx.createGain();
      program.channelCount = 2;
      program.channelCountMode = 'explicit';
      program.channelInterpretation = 'speakers';
      const monitor = ctx.createGain();
      program.connect(monitor).connect(ctx.destination);
      const splitter = ctx.createChannelSplitter(2);
      program.connect(splitter);
      // The analysers feed a silent gain to the speakers: an analyser that nothing pulls is not always processed.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      sink.connect(ctx.destination);
      this.analysers = [0, 1].map((ch) => {
        const a = ctx.createAnalyser();
        a.fftSize = FFT;
        a.smoothingTimeConstant = 0;
        splitter.connect(a, ch);
        a.connect(sink);
        return a;
      });
      this.bufs = [new Float32Array(FFT), new Float32Array(FFT)];
      this.loudness = new LoudnessMeter(ctx.sampleRate, 2);
      this.ctx = ctx;
      this.program = program;
      this.monitor = monitor;
      this.applyMonitor();
    } catch {
      this.failed = true;
    }
    return this.ctx;
  }

  /**
   * Route an element through its gain node (at React's ref callback). An element can only ever get one source node,
   * so the nodes are kept for the element's lifetime: React may detach and attach the same element again (StrictMode
   * does on every mount), and the second attach reconnects the nodes it already has.
   */
  attach(el: HTMLMediaElement): void {
    // The context is made at the first Play (a user gesture), so it starts running in every browser (Safari keeps
    // a context made earlier suspended); until then elements wait here and play through their own volume.
    if (!this.ctx) { if (!this.failed) this.pending.add(el); return; }
    const ctx = this.ctx;
    if (!this.program) return;
    let r = this.routes.get(el);
    if (!r) {
      try {
        const source = ctx.createMediaElementSource(el);
        const gain = ctx.createGain();
        gain.gain.value = 0;
        source.connect(gain);
        r = { source, gain };
        this.routes.set(el, r);
      } catch {
        // Not routable (Web Audio refused it): it plays through el.volume.
        return;
      }
    }
    if (this.connected.has(el)) return;
    r.gain.connect(this.program);
    this.connected.add(el);
    el.volume = 1;
    routeVolume(el, (v) => {
      const g = this.routes.get(el)?.gain;
      if (!g || !this.ctx) return;
      g.gain.cancelScheduledValues(this.ctx.currentTime);
      g.gain.setTargetAtTime(v, this.ctx.currentTime, GAIN_TIME);
    });
  }

  /** React detached the element: it no longer reaches the program (its nodes stay, in case it comes back). */
  detach(el: HTMLMediaElement): void {
    this.pending.delete(el);
    const r = this.routes.get(el);
    if (!r || !this.connected.has(el)) return;
    r.gain.disconnect();
    r.gain.gain.value = 0;
    this.connected.delete(el);
    routeVolume(el, null);
  }

  /** Start (or resume) the context: call from a user gesture (Play). */
  resume(): void {
    const ctx = this.context();
    if (!ctx) return;
    for (const el of this.pending) this.attach(el);
    this.pending.clear();
    if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
  }

  /** The viewer's monitor volume (0–1) and mute; not part of the program, so the meter does not change. */
  setMonitor(volume: number, muted: boolean): void {
    this.monitorLevel = { volume, muted };
    this.applyMonitor();
  }

  private applyMonitor(): void {
    if (!this.monitor || !this.ctx) return;
    const v = this.monitorLevel.muted ? 0 : this.monitorLevel.volume;
    this.monitor.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
  }

  /** Silence the program (shuttle at other speeds than 1×). */
  setProgramMuted(muted: boolean): void {
    if (!this.program || !this.ctx) return;
    this.program.gain.setTargetAtTime(muted ? 0 : 1, this.ctx.currentTime, GAIN_TIME);
  }

  /** Playback starts: measure from now (what the analysers hold from before is not new). */
  startMetering(): void {
    if (this.ctx) this.lastTime = this.ctx.currentTime;
  }

  /** Read the samples that arrived since the last call into the meter (on an animation frame while playing). */
  analyse(now = performance.now()): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || !this.loudness) return;
    const t = ctx.currentTime;
    const n = Math.min(FFT, Math.round((t - this.lastTime) * ctx.sampleRate));
    if (n <= 0) return;
    this.lastTime = t;
    const fresh = this.analysers.map((a, ch) => {
      const buf = this.bufs[ch]!;
      a.getFloatTimeDomainData(buf);
      return buf.subarray(FFT - n);
    });
    this.loudness.push(fresh);
    fresh.forEach((f, ch) => this.peaks[ch]!.push(f, now));
    this.publish(true);
  }

  private publish(live: boolean): void {
    this.meter.set({
      channels: this.peaks.map((p) => ({ level: p.level, hold: p.hold, over: p.over })),
      momentary: this.loudness?.momentary() ?? -Infinity,
      shortTerm: this.loudness?.shortTerm() ?? -Infinity,
      integrated: this.loudness?.integrated() ?? -Infinity,
      live,
    });
  }

  /** Clear the integrated loudness, the peak holds and the overs. */
  resetMeter(): void {
    this.loudness?.reset();
    for (const p of this.peaks) p.reset();
    this.publish(this.ctx?.state === 'running');
  }

  close(): void {
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }
}
